import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Icons } from '../utils/icons';
import api, { BOT_USERNAME } from '../utils/api';
import { useToast } from '../contexts/ToastContext';
import { fontStack } from '../styles/shared';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmtUsd = (v) => `$${(v || 0).toFixed(2)}`;
const fmtCompact = (v) => {
    if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
    if (v >= 1000) return `$${(v / 1000).toFixed(1)}K`;
    return `$${v.toFixed(2)}`;
};
const localDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const purchaseLabel = (t) => {
    if (t === 'subscription') return 'Subscription';
    if (t === 'ppv') return 'Message';
    if (t === 'tip') return 'Tip';
    if (t === 'chargeback') return 'Chargeback';
    if (t === 'refund') return 'Refund';
    if (t === 'trial') return 'Trial';
    return 'Spin Purchase';
};
const purchaseColor = (t) => {
    if (t === 'subscription') return '#10b981';
    if (t === 'ppv') return '#f59e0b';
    if (t === 'tip') return '#8b5cf6';
    if (t === 'chargeback' || t === 'refund') return '#ef4444';
    if (t === 'trial') return '#9ca3af';
    return '#2F6BFF';
};

// ─── Component ──────────────────────────────────────────────────────────────
export default function OverviewTab({ wheels = [], username, onNavigate, isMobile }) {
    const toast = useToast();
    const [loading, setLoading] = useState(true);
    const [onboarding, setOnboarding] = useState(null); // null = loading, false = all complete
    const [onboardingCollapsed, setOnboardingCollapsed] = useState(null); // null = auto (collapse if KYC done)
    const [dateFilter, setDateFilter] = useState({ preset: '30d', start: null, end: null });
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [accountCreatedAt, setAccountCreatedAt] = useState(null);
    const datePickerRef = useRef(null);
    const [earnings, setEarnings] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [dailyEarnings, setDailyEarnings] = useState([]);
    const [subscriptionEarnings, setSubscriptionEarnings] = useState(null);
    const [totalEarnedAllUsd, setTotalEarnedAllUsd] = useState(0);
    const [disputeStats, setDisputeStats] = useState(null);
    const [starsPerUsd, setStarsPerUsd] = useState(77);
    const [adminSharePct, setAdminSharePct] = useState(0.85);
    const [chartTooltip, setChartTooltip] = useState(null);
    const chartContainerRef = useRef(null);

    const s2u = useCallback((stars) => (stars || 0) / starsPerUsd, [starsPerUsd]);

    // ─── Click outside to close picker ──────────────────────────────────────
    useEffect(() => {
        const handler = (e) => {
            if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
                setShowDatePicker(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // ─── Onboarding Status ──────────────────────────────────────────────────
    const refreshOnboarding = useCallback(() => {
        api.get('/api/dashboard/onboarding-status').then(res => {
            const d = res.data;
            if (d.telegram_linked && d.channel_connected && d.has_sale) {
                setOnboarding(false);
            } else {
                setOnboarding(d);
            }
        }).catch(() => setOnboarding(false));
    }, []);

    useEffect(() => { refreshOnboarding(); }, [refreshOnboarding]);

    // Re-check onboarding when wheels change (channel added/removed)
    useEffect(() => { if (onboarding) refreshOnboarding(); }, [wheels.length]);

    // Re-check onboarding when tab regains focus (e.g. returning from Whop KYC)
    useEffect(() => {
        const onFocus = () => { if (onboarding) refreshOnboarding(); };
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [onboarding, refreshOnboarding]);

    // ─── Data Loading ────────────────────────────────────────────────────────
    useEffect(() => { loadEarnings(); }, []);

    useEffect(() => {
        if (dateFilter.preset === 'custom' && dateFilter.start && dateFilter.end) {
            const today = new Date(); today.setHours(0, 0, 0, 0);
            const daysToStart = Math.ceil((today - dateFilter.start) / (1000 * 60 * 60 * 24)) + 1;
            loadDailyEarnings(Math.min(daysToStart * 2, 365));
        } else {
            const fetchDays = { today: 7, yesterday: 7, '7d': 14, '30d': 60, '90d': 180, '365d': 365 };
            loadDailyEarnings(fetchDays[dateFilter.preset] || 60);
        }
    }, [dateFilter]);

    // DEMO MODE: inject fake data for hdurumodel3 promo video
    const isDemoUser = username === 'hdurumodel3';

    const loadEarnings = async () => {
        try {
            const res = await api.post('/api/dashboard/earnings', { token: localStorage.getItem('token') });
            if (isDemoUser) {
                // Fake earnings for promo
                setEarnings({ total_earned: 847600, last_30_days: 312400, pending_stars: 45200, available_stars: 0, total_withdrawn: 0 });
                const fakeTxns = [];
                const types = ['subscription','subscription','ppv','subscription','tip','subscription','ppv','subscription','subscription','tip'];
                const names = ['Emma','Liam','Olivia','Noah','Ava','James','Sophia','Mason','Isabella','Ethan','Charlotte','Lucas','Amelia','Henry','Harper','Alexander','Mia','Benjamin','Evelyn','Jack','Lily','Jack','Zoe','Ryan','Grace','Chloe','Adam','Ella','Leo','Nora'];
                const channels = ['-1003740810004','-1003740810004','-1003740810004','-1003741249216','-1003740810004','-1003741249216','-1003740810004','-1003740810004','-1003741249216','-1003740810004'];
                for (let i = 0; i < 50; i++) {
                    const d = new Date();
                    if (i < 5) { d.setMinutes(d.getMinutes() - i * 8); } // first 5 within last hour
                    else { d.setDate(d.getDate() - Math.floor((i-4) * 0.6)); d.setHours(8 + Math.floor(Math.random()*14), Math.floor(Math.random()*60)); }
                    const type = types[i%10];
                    const amt = type === 'tip' ? [5,10,15,25,50][i%5] : type === 'ppv' ? [5,10,15,20,25][i%5] : [9.99,14.99,19.99,24.99,29.99][i%5];
                    fakeTxns.push({ transaction_id: `demo_${i}`, user_id: 300000+i, buyer_username: names[i%30].toLowerCase()+((i*7+13)%99), buyer_name: names[i%30], admin_username: 'hdurumodel3', channel_username: channels[i%10], amount_usd: amt, amount_stars: 0, admin_share: 0, platform_fee: 0, spins_purchased: 0, purchase_type: type, payment_method: 'card', status: 'completed', created_at: d.toISOString() });
                }
                fakeTxns.sort((a,b) => new Date(b.created_at) - new Date(a.created_at));
                setTransactions(fakeTxns);
                setStarsPerUsd(77);
                setAdminSharePct(0.85);
                setSubscriptionEarnings({ active_subscribers: 593, monthly_revenue: 8450 });
                setTotalEarnedAllUsd(23400);
                setAccountCreatedAt(new Date('2025-06-15'));
            } else {
                setEarnings(res.data.earnings);
                setTransactions(res.data.transactions || []);
                setStarsPerUsd(res.data.stars_per_usd || 77);
                setAdminSharePct((res.data.admin_share_percent ?? 80) / 100);
                if (res.data.subscription_earnings) setSubscriptionEarnings(res.data.subscription_earnings);
                if (res.data.total_earned_all_usd != null) setTotalEarnedAllUsd(res.data.total_earned_all_usd);
                if (res.data.account_created_at) setAccountCreatedAt(new Date(res.data.account_created_at));
                if (res.data.dispute_stats) setDisputeStats(res.data.dispute_stats);
            }
        } catch (e) { /* silent */ }
        finally { setLoading(false); }
    };

    const loadDailyEarnings = async (days = 30) => {
        try {
            const res = await api.get(`/api/dashboard/daily-earnings?days=${days}`);
            if (isDemoUser) {
                // Fake daily earnings: realistic organic growth with natural variation
                const fakeDaily = [];
                const now = new Date();
                let prev = 310;
                for (let i = days; i >= 0; i--) {
                    const d = new Date(now); d.setDate(d.getDate() - i);
                    const dayOfWeek = d.getDay();
                    // Weekends earn ~20% less
                    const weekendDip = (dayOfWeek === 0 || dayOfWeek === 6) ? 0.8 : 1.0;
                    // Strong upward trend: first week ~250, last week ~480
                    const progress = (days - i) / days;
                    const trend = 230 + progress * progress * 280;
                    // Smooth random walk: prev ± small step
                    const step = (Math.sin(i * 1.1) * 30 + Math.cos(i * 0.7) * 20 + Math.sin(i * 2.3) * 15);
                    const target = trend + step;
                    // Blend with previous for smoothness
                    prev = prev * 0.4 + target * 0.6;
                    const earned = Math.round(Math.max(220, Math.min(520, prev * weekendDip)));
                    const txCount = Math.round(earned / 14) + Math.floor(Math.sin(i) * 3);
                    fakeDaily.push({ date: d.toISOString().slice(0,10), earned: 0, crypto_usd: earned, transactions: Math.max(8, txCount) });
                }
                setDailyEarnings(fakeDaily);
            } else {
                setDailyEarnings(res.data.daily_earnings || []);
            }
        } catch (e) { /* silent */ }
    };

    // ─── Computed Data ───────────────────────────────────────────────────────
    const filterLabel = useMemo(() => {
        if (dateFilter.preset === 'today') return 'Today';
        if (dateFilter.preset === 'yesterday') return 'Yesterday';
        if (dateFilter.preset === '7d') return 'Last 7 days';
        if (dateFilter.preset === '30d') return 'Last 30 days';
        if (dateFilter.preset === '90d') return 'Last 90 days';
        if (dateFilter.preset === '365d') return 'Last 365 days';
        if (dateFilter.preset === 'custom' && dateFilter.start && dateFilter.end) {
            const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            return `${fmt(dateFilter.start)} – ${fmt(dateFilter.end)}`;
        }
        return 'Last 30 days';
    }, [dateFilter]);

    const chartData = useMemo(() => {
        const mkVal = (d) => s2u(d.earned || 0) + (d.crypto_usd || 0);
        const today = new Date(); today.setHours(0, 0, 0, 0);

        // Today / Yesterday: build 24 hourly buckets from raw transactions
        if (dateFilter.preset === 'today' || dateFilter.preset === 'yesterday') {
            const targetDate = new Date(today);
            if (dateFilter.preset === 'yesterday') targetDate.setDate(today.getDate() - 1);
            const targetDateStr = localDateStr(targetDate);
            const hourly = Array.from({ length: 24 }, (_, h) => ({ hour: h, earned_usd: 0, transactions: 0, isHourly: true }));
            transactions.forEach(tx => {
                if (!tx.created_at) return;
                const txDate = new Date(tx.created_at);
                if (localDateStr(txDate) !== targetDateStr) return;
                const h = txDate.getHours();
                const isUsd = tx.payment_method === 'crypto' || tx.payment_method === 'card';
                const amount = isUsd ? parseFloat(tx.amount_usd || 0) * adminSharePct : (tx.admin_share || 0) / starsPerUsd;
                hourly[h].earned_usd += amount;
                hourly[h].transactions += 1;
            });
            return hourly;
        }

        const lookup = {};
        dailyEarnings.forEach(d => { lookup[d.date] = d; });
        let startDate, numDays;
        if (dateFilter.preset === 'custom' && dateFilter.start && dateFilter.end) {
            startDate = new Date(dateFilter.start); startDate.setHours(0, 0, 0, 0);
            numDays = Math.ceil((new Date(dateFilter.end) - startDate) / (1000 * 60 * 60 * 24)) + 1;
        } else {
            const n = { '7d': 7, '30d': 30, '90d': 90, '365d': 365 }[dateFilter.preset] || 30;
            startDate = new Date(today); startDate.setDate(today.getDate() - (n - 1)); numDays = n;
        }
        const result = [];
        for (let i = 0; i < numDays; i++) {
            const dt = new Date(startDate); dt.setDate(startDate.getDate() + i);
            const dateStr = localDateStr(dt);
            const d = lookup[dateStr] || { date: dateStr, earned: 0, crypto_usd: 0, transactions: 0 };
            result.push({ ...d, date: dateStr, earned_usd: mkVal(d) });
        }
        return result;
    }, [dailyEarnings, dateFilter, s2u, transactions, starsPerUsd, adminSharePct]);

    const periodRevenue = useMemo(() => chartData.reduce((sum, d) => sum + d.earned_usd, 0), [chartData]);

    const prevPeriodRevenue = useMemo(() => {
        const mkVal = (d) => s2u(d.earned || 0) + (d.crypto_usd || 0);
        const lookup = {};
        dailyEarnings.forEach(d => { lookup[d.date] = d; });
        const today = new Date(); today.setHours(0, 0, 0, 0);
        let curStart, curLen;
        if (dateFilter.preset === 'custom' && dateFilter.start && dateFilter.end) {
            curStart = new Date(dateFilter.start); curStart.setHours(0, 0, 0, 0);
            curLen = Math.ceil((new Date(dateFilter.end) - curStart) / (1000 * 60 * 60 * 24)) + 1;
        } else if (dateFilter.preset === 'today') {
            curStart = new Date(today); curLen = 1;
        } else if (dateFilter.preset === 'yesterday') {
            curStart = new Date(today); curStart.setDate(today.getDate() - 1); curLen = 1;
        } else {
            const n = { '7d': 7, '30d': 30, '90d': 90, '365d': 365 }[dateFilter.preset] || 30;
            curStart = new Date(today); curStart.setDate(today.getDate() - (n - 1)); curLen = n;
        }
        const prevEnd = new Date(curStart); prevEnd.setDate(curStart.getDate() - 1);
        const prevStart = new Date(prevEnd); prevStart.setDate(prevEnd.getDate() - (curLen - 1));
        let sum = 0;
        for (let i = 0; i < curLen; i++) {
            const dt = new Date(prevStart); dt.setDate(prevStart.getDate() + i);
            const d = lookup[localDateStr(dt)];
            if (d) sum += mkVal(d);
        }
        return sum;
    }, [dailyEarnings, dateFilter, s2u, adminSharePct]);

    const revenueChange = prevPeriodRevenue > 0
        ? ((periodRevenue - prevPeriodRevenue) / prevPeriodRevenue * 100)
        : (periodRevenue > 0 ? 100 : 0);

    const activeSubscribers = subscriptionEarnings?.active_subscribers || 0;
    const channelCount = wheels.length;

    const channelEarnings = useMemo(() => {
        const byChannel = {};
        transactions.forEach(t => {
            const ch = t.channel_username || 'Unknown';
            if (!byChannel[ch]) byChannel[ch] = { earned_usd: 0, count: 0 };
            const isUsd = t.payment_method === 'crypto' || t.payment_method === 'card';
            byChannel[ch].earned_usd += isUsd ? parseFloat(t.amount_usd || 0) : (t.admin_share || 0) / starsPerUsd;
            byChannel[ch].count += 1;
        });
        return byChannel;
    }, [transactions, starsPerUsd, adminSharePct]);

    // ─── Render ──────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 40px' }}>
                <img src="/logo-light.webp" alt="Loading" style={{ height: '28px', opacity: 0.5, animation: 'pulse 1.5s ease-in-out infinite' }} />
            </div>
        );
    }

    return (
        <div style={{ fontFamily: fontStack, maxWidth: '1100px', margin: '0 auto' }}>
            {/* ─── Onboarding Tracker ──────────────────────────────────────── */}
            {onboarding && onboarding !== false && (() => {
                const steps = [
                    { key: 'telegram_linked', label: 'Link your Telegram', desc: 'Connect your Telegram account to get started', action: () => {
                        api.post('/api/dashboard/account-info', { token: localStorage.getItem('token') }).then(res => {
                            const code = res.data.connect_code;
                            if (code) {
                                window.open(`https://t.me/${BOT_USERNAME}?start=connect_${code}`, '_blank', 'noopener,noreferrer');
                            }
                        }).catch(() => toast.error('Failed to generate link'));
                    }, actionLabel: 'Link Telegram' },
                    { key: 'channel_connected', label: 'Connect your Telegram channel', desc: 'Check your Telegram for the channel selection message', action: () => {
                        api.post('/api/dashboard/request-add-channel', { token: localStorage.getItem('token') }).then(() => {
                            toast.success('Check your Telegram! Open the message to select your channel.');
                        }).catch(err => toast.error(err.response?.data?.error || 'Failed to send channel request'));
                    }, actionLabel: 'Connect Channel' },
                    { key: 'has_sale', label: 'Start earning', desc: 'Make your first sale to complete setup', action: null, actionLabel: null },
                ];
                const completed = steps.filter(s => onboarding[s.key]).length;
                // Auto-collapse once channel is connected
                const isCollapsed = onboardingCollapsed !== null ? onboardingCollapsed : onboarding.channel_connected;
                const progress = completed / steps.length;
                return (
                    <div style={{
                        background: 'var(--card-bg)', border: '1px solid var(--card-border)',
                        borderRadius: '16px', padding: isMobile ? '20px' : '28px', marginBottom: '28px',
                        overflow: 'hidden',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}
                             onClick={() => setOnboardingCollapsed(!isCollapsed)}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                                    <h2 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text)', margin: 0 }}>
                                        Welcome to Privly
                                    </h2>
                                    {completed === steps.length && (
                                        <span style={{ fontSize: '11px', fontWeight: 600, color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '2px 8px', borderRadius: '10px' }}>All done!</span>
                                    )}
                                </div>
                                <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '0 0 10px' }}>
                                    Let's set up your account and start earning
                                </p>
                                {/* Progress bar */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{ flex: 1, height: '6px', borderRadius: '3px', background: 'var(--border)', overflow: 'hidden' }}>
                                        <div style={{
                                            height: '100%', borderRadius: '3px',
                                            background: completed === steps.length ? '#10b981' : 'linear-gradient(90deg, #00AEEF, #10b981)',
                                            width: `${progress * 100}%`,
                                            transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                                        }} />
                                    </div>
                                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#10b981', whiteSpace: 'nowrap' }}>
                                        {completed}/{steps.length}
                                    </span>
                                </div>
                            </div>
                            <div style={{
                                width: '32px', height: '32px', borderRadius: '8px', flexShrink: 0, marginLeft: '16px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                background: 'var(--bg)', border: '1px solid var(--border)',
                                transition: 'background 0.2s',
                            }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                                     style={{ transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)', transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)' }}>
                                    <polyline points="6 9 12 15 18 9"/>
                                </svg>
                            </div>
                        </div>
                        <div style={{
                            maxHeight: isCollapsed ? '0px' : '600px',
                            opacity: isCollapsed ? 0 : 1,
                            overflow: 'hidden',
                            transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease',
                        }}>
                            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '8px', marginTop: '16px' }}>
                            {steps.map((step, i) => {
                                const done = onboarding[step.key];
                                const isNext = !done && steps.slice(0, i).every(s => onboarding[s.key]);
                                return (
                                    <div key={step.key} style={{
                                        display: 'flex', alignItems: 'center', gap: '14px',
                                        padding: i === steps.length - 1 ? '16px 4px 0' : '16px 4px',
                                        borderBottom: i < steps.length - 1 ? '1px solid var(--border)' : 'none',
                                        opacity: done ? 0.6 : 1,
                                        transition: 'opacity 0.2s',
                                    }}>
                                        <div style={{
                                            width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '13px', fontWeight: 700,
                                            background: done ? 'rgba(16,185,129,0.12)' : isNext ? 'rgba(0,174,239,0.12)' : 'var(--bg)',
                                            color: done ? '#10b981' : isNext ? '#00AEEF' : 'var(--text-muted)',
                                            border: `1px solid ${done ? 'rgba(16,185,129,0.25)' : isNext ? 'rgba(0,174,239,0.25)' : 'var(--border)'}`,
                                            transition: 'all 0.3s',
                                        }}>
                                            {done ? (
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                            ) : (
                                                <span>{String(i + 1).padStart(2, '0')}</span>
                                            )}
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: '14px', fontWeight: 600, color: done ? 'var(--text-muted)' : 'var(--text)', textDecoration: done ? 'line-through' : 'none', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                {step.label}
                                                {step.tooltip && (
                                                    <span title={step.tooltip} style={{
                                                        width: '16px', height: '16px', borderRadius: '50%',
                                                        background: 'var(--border)', color: 'var(--text-muted)',
                                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                        fontSize: '10px', fontWeight: 700, cursor: 'help', flexShrink: 0,
                                                    }}>?</span>
                                                )}
                                            </div>
                                            {isNext && (
                                                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{step.desc}</div>
                                            )}
                                        </div>
                                        {isNext && step.action && (
                                            <button onClick={step.action} style={{
                                                background: '#00AEEF', color: '#fff', border: 'none',
                                                borderRadius: '8px', padding: '8px 16px', fontSize: '13px',
                                                fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                                                transition: 'transform 0.15s, box-shadow 0.15s',
                                            }}
                                            onMouseDown={e => e.currentTarget.style.transform = 'scale(0.96)'}
                                            onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
                                            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
                                                {step.actionLabel}
                                            </button>
                                        )}
                                        {!done && !isNext && (
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.4 }}><polyline points="9 18 15 12 9 6"/></svg>
                                        )}
                                    </div>
                                );
                            })}
                            </div>
                        </div>
                    </div>
                );
            })()}
            {/* ─── Header ──────────────────────────────────────────────────── */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '28px', flexWrap: 'wrap', gap: '16px' }}>
                <div>
                    <h1 style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 700, color: 'var(--text)', margin: '0 0 4px' }}>
                        Hello, @{username}
                    </h1>
                    <p style={{ fontSize: '14px', color: 'var(--text-muted)', margin: 0 }}>Welcome back to your dashboard.</p>
                </div>

                {/* Date Range Picker trigger */}
                <div ref={datePickerRef} style={{ position: 'relative', flexShrink: 0 }}>
                    <button
                        onClick={() => setShowDatePicker(v => !v)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            padding: '8px 14px', borderRadius: '10px',
                            border: `1px solid ${showDatePicker ? 'var(--accent)' : 'var(--border)'}`,
                            background: 'var(--card-bg)', cursor: 'pointer', fontFamily: fontStack,
                            fontSize: '13px', fontWeight: 600, color: 'var(--text)',
                            boxShadow: showDatePicker ? '0 0 0 3px rgba(0,174,239,0.12)' : '0 1px 3px rgba(0,0,0,0.04)',
                            transition: 'all 0.15s ease',
                        }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                        </svg>
                        <span>{filterLabel}</span>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text-muted)', transition: 'transform 0.15s ease', transform: showDatePicker ? 'rotate(180deg)' : 'none', flexShrink: 0 }}>
                            <polyline points="6 9 12 15 18 9"/>
                        </svg>
                    </button>
                    {showDatePicker && (
                        <DateRangePicker
                            filter={dateFilter}
                            onChange={(f) => { setDateFilter(f); setShowDatePicker(false); }}
                            onClose={() => setShowDatePicker(false)}
                            minDate={accountCreatedAt}
                            isMobile={isMobile}
                        />
                    )}
                </div>
            </div>

            {/* ─── Stat Cards ──────────────────────────────────────────────── */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: isMobile ? '10px' : '16px', marginBottom: '24px' }}>
                <StatCard
                    label={`Revenue (${filterLabel})`}
                    value={fmtCompact(periodRevenue)}
                    change={revenueChange}
                    icon={<Icons.DollarSign />}
                    accent="#10b981"
                />
                <StatCard
                    label="Active Subscribers"
                    value={activeSubscribers.toLocaleString()}
                    icon={<Icons.Users />}
                    accent="#2F6BFF"
                />
                <StatCard
                    label="Channels"
                    value={channelCount.toString()}
                    icon={<Icons.Grid />}
                    accent="#8b5cf6"
                />
                <StatCard
                    label="Total Revenue"
                    value={fmtCompact(totalEarnedAllUsd)}
                    icon={<Icons.Wallet />}
                    accent="#f59e0b"
                />
            </div>

            {disputeStats && (disputeStats.successful_30d > 0 || disputeStats.disputed_30d > 0) && (() => {
                const ratio = disputeStats.ratio_30d_percent || 0;
                const color = ratio >= 1 ? '#ef4444' : ratio >= 0.65 ? '#f59e0b' : '#10b981';
                const label = ratio >= 1 ? 'Critical' : ratio >= 0.65 ? 'Warning' : 'Healthy';
                const hint = ratio >= 1
                    ? 'Card networks flag accounts above 1%. High risk of processor action — review your refund/chargeback causes immediately.'
                    : ratio >= 0.65
                        ? 'Card networks start issuing warnings above 0.65%. Watch your refund rate closely.'
                        : 'Your payment health is good. Keep it up.';
                return (
                    <div style={{
                        background: 'var(--card-bg)', border: `1px solid ${color}33`, borderRadius: '12px',
                        padding: isMobile ? '14px 16px' : '16px 20px', marginBottom: '24px',
                        display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap',
                    }}>
                        <div style={{ flex: '0 0 auto' }}>
                            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Dispute Ratio (30d)</div>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                                <span style={{ fontSize: '26px', fontWeight: 700, color }}>{ratio.toFixed(2)}%</span>
                                <span style={{ fontSize: '11px', fontWeight: 700, color, background: `${color}15`, padding: '3px 8px', borderRadius: '6px', letterSpacing: '0.3px', textTransform: 'uppercase' }}>{label}</span>
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                                {disputeStats.disputed_30d} disputed / {disputeStats.successful_30d} successful payments
                            </div>
                        </div>
                        <div style={{ flex: '1 1 200px', minWidth: '200px', fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                            {hint}
                        </div>
                    </div>
                );
            })()}

            {/* ─── Chart + Top Channels ────────────────────────────────────── */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 340px', gap: '16px', marginBottom: '24px', alignItems: 'start' }}>
                {/* Revenue Chart */}
                <div style={cardStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)', margin: 0 }}>Revenue Overview</h3>
                        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{filterLabel}</span>
                    </div>
                    <RevenueChart
                        chartData={chartData}
                        chartTooltip={chartTooltip}
                        setChartTooltip={setChartTooltip}
                        chartContainerRef={chartContainerRef}
                    />
                </div>

                {/* Top Channels */}
                <div style={cardStyle}>
                    <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)', margin: '0 0 16px' }}>Top Channels</h3>
                    {wheels.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '30px 16px', color: 'var(--text-muted)', fontSize: '13px' }}>
                            <Icons.Grid />
                            <p style={{ marginTop: '8px' }}>No channels yet</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {[...wheels].sort((a, b) => (channelEarnings[b.channel_username]?.earned_usd || 0) - (channelEarnings[a.channel_username]?.earned_usd || 0)).slice(0, 3).map((w, idx) => {
                                const ch = channelEarnings[w.channel_username];
                                const earned = ch?.earned_usd || 0;
                                const txCount = ch?.count || 0;
                                const maxEarned = Math.max(...wheels.map(ww => channelEarnings[ww.channel_username]?.earned_usd || 0), 0.01);
                                const barPct = Math.max(5, (earned / maxEarned) * 100);
                                return (
                                    <div key={w.channel_username} style={{ padding: '12px', borderRadius: '10px', position: 'relative', overflow: 'hidden', border: '1px solid var(--border)', transition: 'all 0.15s ease', cursor: 'pointer' }}
                                        onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(47,107,255,0.08)'; }}
                                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}>
                                        <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: `${barPct}%`, background: earned > 0 ? 'rgba(47,107,255,0.04)' : 'transparent', transition: 'width 0.4s ease', borderRadius: '10px' }} />
                                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <div style={{ width: '38px', height: '38px', borderRadius: '10px', overflow: 'hidden', flexShrink: 0, background: `linear-gradient(135deg, ${['#2F6BFF','#8b5cf6','#f59e0b','#10b981','#ef4444'][idx%5]}, ${['#1D5AE6','#7c3aed','#d97706','#059669','#dc2626'][idx%5]})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '15px', fontWeight: 700 }}>
                                                {(w.title || w.channel_username || 'C')[0].toUpperCase()}
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{w.title || w.channel_username}</p>
                                                <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: '2px 0 0' }}>{txCount} sale{txCount !== 1 ? 's' : ''}</p>
                                            </div>
                                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                                <p style={{ fontSize: '14px', fontWeight: 700, color: earned > 0 ? 'var(--text)' : 'var(--text-muted)', margin: 0 }}>{earned > 0 ? fmtUsd(earned) : '$0.00'}</p>
                                                <p style={{ fontSize: '10px', color: 'var(--text-muted)', margin: '2px 0 0' }}>earned</p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* ─── Recent Payments ─────────────────────────────────────────── */}
            <div style={cardStyle}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)', margin: 0 }}>Recent Payments</h3>
                    <button onClick={() => onNavigate?.('/dashboard/payments')}
                        style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: fontStack, display: 'flex', alignItems: 'center', gap: '4px' }}>
                        View all <span style={{ fontSize: '16px' }}>&rsaquo;</span>
                    </button>
                </div>
                {transactions.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '30px 16px', color: 'var(--text-muted)', fontSize: '13px' }}>No payments yet</div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>Customer</th>
                                    <th style={thStyle}>Type</th>
                                    <th style={thStyle}>Channel</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.slice(0, 5).map((tx, i) => {
                                    const name = tx.buyer_name || tx.buyer_username || (tx.user_id ? `User ${tx.user_id}` : 'Pending Telegram link');
                                    const type = purchaseLabel(tx.purchase_type);
                                    const color = purchaseColor(tx.purchase_type);
                                    const isUsd = tx.payment_method === 'crypto' || tx.payment_method === 'card';
                                    const amount = isUsd ? parseFloat(tx.amount_usd || 0) : (tx.amount_stars || 0) / starsPerUsd;
                                    const timeStr = tx.created_at ? new Date(tx.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
                                    const channelUsername = tx.channel_username || tx.admin_username || '';
                                    const channelWheel = wheels.find(w => w.channel_username === channelUsername);
                                    const channel = channelWheel?.title || channelUsername || '—';
                                    return (
                                        <tr key={tx.id || i} style={{ borderBottom: '1px solid var(--border)' }}>
                                            <td style={tdStyle}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: `${color}15`, color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', flexShrink: 0 }}>
                                                        {(name[0] || '?').toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: 500, color: 'var(--text)' }}>{name}</div>
                                                        {timeStr && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{timeStr}</div>}
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={tdStyle}>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: '6px', background: `${color}12`, color, fontSize: '11px', fontWeight: 600 }}>{type}</span>
                                            </td>
                                            <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>{channel}</td>
                                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: amount < 0 ? '#ef4444' : tx.purchase_type === 'trial' ? '#9ca3af' : 'var(--text)' }}>{tx.purchase_type === 'trial' ? '$0.00' : amount < 0 ? `-${fmtUsd(Math.abs(amount))}` : fmtUsd(amount)}</td>
                                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                                                {tx.purchase_type === 'trial' ? (
                                                    <span style={{ padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, background: 'rgba(156,163,175,0.1)', color: '#9ca3af' }}>
                                                        Trial
                                                    </span>
                                                ) : tx.purchase_type === 'chargeback' || tx.purchase_type === 'refund' ? (
                                                    <span style={{ padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, background: 'rgba(239,68,68,0.1)', color: '#ef4444' }}>
                                                        {tx.purchase_type === 'chargeback' ? 'Chargeback' : 'Refunded'}
                                                    </span>
                                                ) : (
                                                    <span style={{ padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, background: tx.status === 'completed' ? 'rgba(16,185,129,0.1)' : 'rgba(249,115,22,0.1)', color: tx.status === 'completed' ? '#10b981' : '#f97316' }}>
                                                        {tx.status === 'completed' ? 'Completed' : 'Pending'}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Revenue Chart ────────────────────────────────────────────────────────────
function RevenueChart({ chartData, chartTooltip, setChartTooltip, chartContainerRef }) {
    if (chartData.length === 0) return null;

    const maxEarned = Math.max(...chartData.map(d => d.earned_usd), 0.01);
    const chartWidth = 700;
    const chartHeight = 220;
    const padding = { top: 24, right: 16, bottom: 48, left: 52 };
    const innerWidth = chartWidth - padding.left - padding.right;
    const innerHeight = chartHeight - padding.top - padding.bottom;
    const yMin = padding.top;
    const yMax = padding.top + innerHeight;
    const clampY = (v) => Math.max(yMin, Math.min(yMax, v));

    const points = chartData.map((d, i) => ({
        x: padding.left + (i / Math.max(chartData.length - 1, 1)) * innerWidth,
        y: Math.min(yMax - 1, yMax - (d.earned_usd / maxEarned) * innerHeight),
        ...d
    }));

    // Smooth path — handles single point too
    const createSmoothPath = (pts) => {
        if (pts.length === 0) return '';
        if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`;
        let path = `M ${pts[0].x} ${pts[0].y}`;
        // Reduce tension for large datasets to avoid excessive waviness
        const tension = pts.length > 60 ? 8 : 6;
        for (let i = 0; i < pts.length - 1; i++) {
            const p0 = pts[i - 1] || pts[i];
            const p1 = pts[i];
            const p2 = pts[i + 1];
            const p3 = pts[i + 2] || p2;
            const cp1x = p1.x + (p2.x - p0.x) / tension;
            const cp1y = clampY(p1.y + (p2.y - p0.y) / tension);
            const cp2x = p2.x - (p3.x - p1.x) / tension;
            const cp2y = clampY(p2.y - (p3.y - p1.y) / tension);
            path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
        }
        return path;
    };

    const linePath = createSmoothPath(points);
    const areaPath = linePath + ` L ${points[points.length - 1].x} ${yMax} L ${points[0].x} ${yMax} Z`;
    const yLabels = [0, maxEarned * 0.25, maxEarned * 0.5, maxEarned * 0.75, maxEarned];

    const fmtHour = (h) => {
        if (h === 0) return '12am';
        if (h < 12) return `${h}am`;
        if (h === 12) return '12pm';
        return `${h - 12}pm`;
    };

    // X-axis label strategy based on data length
    const renderXLabels = () => {
        const n = chartData.length;
        // Hourly: show 12am, 6am, 12pm, 6pm, 11pm
        if (chartData[0]?.isHourly) {
            return [0, 6, 12, 18, 23].map(h => {
                const x = padding.left + (h / (n - 1)) * innerWidth;
                return <text key={h} x={x} y={yMax + 18} textAnchor="middle" fontSize="9" fill="var(--text-muted)">{fmtHour(h)}</text>;
            });
        }
        if (n > 90) {
            // Monthly labels only
            const shown = new Set();
            return chartData.map((d, i) => {
                const [year, month] = d.date.split('-');
                const key = `${year}-${month}`;
                if (shown.has(key)) return null;
                shown.add(key);
                const x = padding.left + (i / Math.max(n - 1, 1)) * innerWidth;
                const dt = new Date(d.date + 'T12:00:00');
                // Show year only for Jan or if multi-year range
                const isJan = dt.getMonth() === 0;
                const isMultiYear = new Date(chartData[0].date).getFullYear() !== new Date(chartData[n-1].date).getFullYear();
                const label = (isJan && isMultiYear)
                    ? dt.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
                    : dt.toLocaleDateString('en-US', { month: 'short' });
                return <text key={key} x={x} y={yMax + 18} textAnchor="middle" fontSize="9" fill="var(--text-muted)">{label}</text>;
            });
        }
        if (n > 30) {
            // Every ~2 weeks
            return chartData.map((d, i) => {
                if (i % 7 !== 0 && i !== n - 1) return null;
                const x = padding.left + (i / Math.max(n - 1, 1)) * innerWidth;
                const label = new Date(d.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                return <text key={i} x={x} y={yMax + 18} textAnchor="middle" fontSize="9" fill="var(--text-muted)">{label}</text>;
            });
        }
        // Normal: step by 1 or 3
        const step = n <= 10 ? 1 : 3;
        return chartData.map((d, i) => {
            if (i % step !== 0 && i !== n - 1) return null;
            const x = padding.left + (i / Math.max(n - 1, 1)) * innerWidth;
            const label = new Date(d.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            return <text key={i} x={x} y={yMax + 18} textAnchor="middle" fontSize="9" fill="var(--text-muted)">{label}</text>;
        });
    };

    return (
        <div ref={chartContainerRef} style={{ position: 'relative', width: '100%', overflow: 'hidden' }}
            onMouseLeave={() => setChartTooltip(null)}>
            <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} style={{ width: '100%', height: 'auto', display: 'block' }}
                onMouseMove={e => {
                    const container = chartContainerRef.current;
                    if (!container) return;
                    const rect = container.getBoundingClientRect();
                    const svgRect = e.currentTarget.getBoundingClientRect();
                    const scaleX = svgRect.width / chartWidth;
                    const mouseX = (e.clientX - svgRect.left) / scaleX;
                    let closest = points[0], minDist = Infinity;
                    points.forEach(p => { const d = Math.abs(p.x - mouseX); if (d < minDist) { minDist = d; closest = p; } });
                    setChartTooltip({
                        ...closest,
                        svgX: closest.x,
                        x: closest.x * scaleX,
                        y: closest.y * (svgRect.height / chartHeight),
                        containerWidth: rect.width
                    });
                }}>
                <defs>
                    <linearGradient id="ovAreaGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#00AEEF" stopOpacity="0.35" />
                        <stop offset="100%" stopColor="#00AEEF" stopOpacity="0.02" />
                    </linearGradient>
                    <linearGradient id="ovLineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#00AEEF" />
                        <stop offset="100%" stopColor="#10b981" />
                    </linearGradient>
                </defs>

                {/* Grid lines */}
                {yLabels.map((val, i) => {
                    const y = yMax - (val / maxEarned) * innerHeight;
                    return (
                        <g key={i}>
                            <line x1={padding.left} y1={y} x2={chartWidth - padding.right} y2={y} stroke="var(--border)" strokeDasharray="3 3" opacity="0.5" />
                            <text x={padding.left - 8} y={y + 4} textAnchor="end" fontSize="9" fill="var(--text-muted)">${val.toFixed(val >= 1 ? 0 : 2)}</text>
                        </g>
                    );
                })}

                {/* Area + line */}
                {areaPath && (
                    <>
                        <path d={areaPath} fill="url(#ovAreaGrad)" />
                        <path d={linePath} fill="none" stroke="url(#ovLineGrad)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </>
                )}

                {/* Hover */}
                {chartTooltip && (
                    <>
                        <line x1={chartTooltip.svgX} y1={padding.top} x2={chartTooltip.svgX} y2={yMax} stroke="var(--accent)" strokeWidth="1" strokeDasharray="4 3" opacity="0.5" />
                        {points.map((pt, i) => {
                            if (Math.abs((chartTooltip.svgX || 0) - pt.x) >= 2) return null;
                            return <circle key={i} cx={pt.x} cy={pt.y} r="5" fill="#00AEEF" stroke="var(--card-bg)" strokeWidth="3" />;
                        })}
                    </>
                )}

                {/* X labels */}
                {renderXLabels()}
            </svg>

            {/* Tooltip */}
            {chartTooltip && (() => {
                const tw = 130, th = 60, gap = 12;
                const cw = chartTooltip.containerWidth || 500;
                let top = chartTooltip.y - th - gap;
                if (top < 0) top = chartTooltip.y + gap + 10;
                let left = chartTooltip.x + gap + tw < cw - 4 ? chartTooltip.x + gap : chartTooltip.x - gap - tw;
                if (left < 4) left = 4;
                return (
                    <div style={{ position: 'absolute', top, left, width: tw, boxSizing: 'border-box', background: 'var(--card-bg)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid var(--border)', borderRadius: '8px', padding: '8px 12px', boxShadow: '0 4px 16px rgba(0,0,0,0.15)', pointerEvents: 'none', zIndex: 10 }}>
                        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '3px' }}>{chartTooltip.isHourly ? fmtHour(chartTooltip.hour) : new Date(chartTooltip.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                        <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--accent)' }}>${chartTooltip.earned_usd?.toFixed(2)}</div>
                        <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginTop: '3px' }}>{chartTooltip.transactions} sale{chartTooltip.transactions !== 1 ? 's' : ''}</div>
                    </div>
                );
            })()}
        </div>
    );
}

// ─── Date Range Picker ────────────────────────────────────────────────────────
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_NAMES = ['Su','Mo','Tu','We','Th','Fr','Sa'];

function DateRangePicker({ filter, onChange, onClose, minDate, isMobile }) {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const minD = minDate ? new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate()) : null;

    // Initialise calendar to show the 2 months ending on current end date (or today)
    const initLeft = () => {
        const base = filter.end || filter.start || today;
        return new Date(base.getFullYear(), base.getMonth() - 1, 1);
    };
    const [leftMonth, setLeftMonth] = useState(initLeft);
    const rightMonth = new Date(leftMonth.getFullYear(), leftMonth.getMonth() + 1, 1);

    // Temporary selection while user picks dates
    const [tempStart, setTempStart] = useState(filter.preset === 'custom' ? filter.start : null);
    const [tempEnd, setTempEnd] = useState(filter.preset === 'custom' ? filter.end : null);
    const [hoverDate, setHoverDate] = useState(null);

    const PRESETS = [
        { key: 'today', label: 'Today' },
        { key: 'yesterday', label: 'Yesterday' },
        { key: '7d', label: 'Last 7 days' },
        { key: '30d', label: 'Last 30 days' },
        { key: '90d', label: 'Last 90 days' },
        { key: '365d', label: 'Last 365 days' },
    ];

    const handlePreset = (key) => onChange({ preset: key, start: null, end: null });

    const handleDateClick = (date) => {
        if (!tempStart || (tempStart && tempEnd)) {
            setTempStart(date);
            setTempEnd(null);
        } else {
            const [s, e] = date < tempStart ? [date, tempStart] : [tempStart, date];
            setTempStart(s);
            setTempEnd(e);
        }
    };

    const handleApply = () => {
        if (tempStart && tempEnd) onChange({ preset: 'custom', start: tempStart, end: tempEnd });
    };

    const isInRange = (date) => {
        const s = tempStart;
        const e = tempEnd || hoverDate;
        if (!s || !date || !e) return false;
        const lo = s < e ? s : e;
        const hi = s < e ? e : s;
        return date > lo && date < hi;
    };
    const isStartDate = (date) => date && tempStart && localDateStr(date) === localDateStr(tempStart);
    const isEndDate = (date) => date && tempEnd && localDateStr(date) === localDateStr(tempEnd);

    const renderMonth = (monthDate, showPrev, showNext) => {
        const year = monthDate.getFullYear();
        const month = monthDate.getMonth();
        const firstDay = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const cells = [];
        for (let i = 0; i < firstDay; i++) cells.push(null);
        for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

        return (
            <div style={{ minWidth: '210px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                    {showPrev ? (
                        <button onClick={() => setLeftMonth(new Date(leftMonth.getFullYear(), leftMonth.getMonth() - 1, 1))}
                            style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '6px', width: '28px', height: '28px', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>
                            ‹
                        </button>
                    ) : <div style={{ width: '28px' }} />}
                    <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>
                        {MONTH_NAMES[month]} {year}
                    </span>
                    {showNext ? (
                        <button onClick={() => setLeftMonth(new Date(leftMonth.getFullYear(), leftMonth.getMonth() + 1, 1))}
                            style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '6px', width: '28px', height: '28px', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>
                            ›
                        </button>
                    ) : <div style={{ width: '28px' }} />}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: '4px' }}>
                    {DAY_NAMES.map(d => (
                        <div key={d} style={{ textAlign: 'center', fontSize: '10px', fontWeight: 600, color: 'var(--text-muted)', padding: '2px 0' }}>{d}</div>
                    ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px' }}>
                    {cells.map((date, i) => {
                        if (!date) return <div key={`e${i}`} />;
                        const isFuture = date > today;
                        const isBefore = minD ? date < minD : false;
                        const disabled = isFuture || isBefore;
                        const isStart = isStartDate(date);
                        const isEnd = isEndDate(date);
                        const inRange = isInRange(date);
                        const isTodayDate = localDateStr(date) === localDateStr(today);
                        return (
                            <div key={i}
                                onClick={() => !disabled && handleDateClick(date)}
                                onMouseEnter={() => tempStart && !tempEnd && !disabled && setHoverDate(date)}
                                onMouseLeave={() => setHoverDate(null)}
                                style={{
                                    textAlign: 'center', padding: '6px 2px', borderRadius: '6px',
                                    fontSize: '12px', cursor: disabled ? 'default' : 'pointer',
                                    fontWeight: (isStart || isEnd) ? 700 : isTodayDate ? 600 : 400,
                                    background: (isStart || isEnd) ? 'var(--text)' : inRange ? 'rgba(0,174,239,0.12)' : 'transparent',
                                    color: (isStart || isEnd) ? 'var(--card-bg)' : disabled ? 'var(--text-muted)' : isTodayDate ? 'var(--accent)' : 'var(--text)',
                                    opacity: disabled ? 0.3 : 1,
                                    transition: 'background 0.1s',
                                    userSelect: 'none',
                                }}>
                                {date.getDate()}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // Footer selection label
    const fmtShort = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const selectionLabel = tempStart && tempEnd
        ? `${fmtShort(tempStart)} – ${fmtShort(tempEnd)}`
        : tempStart
            ? `${fmtShort(tempStart)} – select end date`
            : 'Select a date range';

    return (
        <div style={{
            position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 1000,
            background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '14px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.14)', overflow: 'hidden',
            fontFamily: fontStack,
        }}>
            <div style={{ display: 'flex' }}>
                {/* Preset list */}
                <div style={{ minWidth: '170px', borderRight: '1px solid var(--border)', padding: '8px 0' }}>
                    {PRESETS.map(p => {
                        const active = filter.preset === p.key;
                        return (
                            <button key={p.key} onClick={() => handlePreset(p.key)}
                                style={{
                                    display: 'block', width: '100%', textAlign: 'left',
                                    padding: '9px 16px', background: active ? 'rgba(0,174,239,0.08)' : 'none',
                                    border: 'none', cursor: 'pointer', fontFamily: fontStack,
                                    fontSize: '13px', fontWeight: active ? 600 : 400,
                                    color: active ? 'var(--accent)' : 'var(--text-secondary)',
                                    borderLeft: active ? '2px solid var(--accent)' : '2px solid transparent',
                                }}
                                onMouseEnter={e => { if (!active) { e.currentTarget.style.background = 'var(--surface-hover)'; } }}
                                onMouseLeave={e => { e.currentTarget.style.background = active ? 'rgba(0,174,239,0.08)' : 'none'; }}>
                                {p.label}
                            </button>
                        );
                    })}
                </div>

                {/* Calendars */}
                <div style={{ padding: '16px 20px', display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                    {renderMonth(leftMonth, true, false)}
                    <div style={{ width: '1px', background: 'var(--border)', alignSelf: 'stretch', flexShrink: 0 }} />
                    {renderMonth(rightMonth, false, true)}
                </div>
            </div>

            {/* Footer */}
            <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-alt)' }}>
                <span style={{ fontSize: '12px', color: tempStart ? 'var(--text)' : 'var(--text-muted)', fontWeight: tempStart ? 500 : 400 }}>
                    {selectionLabel}
                </span>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={onClose} style={{ padding: '6px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--card-bg)', cursor: 'pointer', fontFamily: fontStack, fontSize: '13px', fontWeight: 500, color: 'var(--text)' }}>
                        Cancel
                    </button>
                    <button onClick={handleApply} disabled={!tempStart || !tempEnd}
                        style={{ padding: '6px 14px', borderRadius: '8px', border: 'none', background: tempStart && tempEnd ? 'var(--text)' : 'var(--border)', cursor: tempStart && tempEnd ? 'pointer' : 'default', fontFamily: fontStack, fontSize: '13px', fontWeight: 600, color: tempStart && tempEnd ? 'var(--card-bg)' : 'var(--text-muted)', transition: 'background 0.15s' }}>
                        Apply
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Stat Card ───────────────────────────────────────────────────────────────
function StatCard({ label, value, change, icon, accent }) {
    return (
        <div style={{ ...cardStyle, transition: 'all 0.2s ease', cursor: 'default' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: 500 }}>{label}</span>
                <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: `${accent}12`, color: accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>{icon}</div>
            </div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--text)', lineHeight: 1.2 }}>{value}</div>
            {change != null && (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '8px', fontSize: '12px', fontWeight: 600, color: change >= 0 ? '#10b981' : '#ef4444' }}>
                    <span>{change >= 0 ? '↑' : '↓'}</span>
                    <span>{Math.abs(change).toFixed(1)}%</span>
                    <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>vs prev period</span>
                </div>
            )}
        </div>
    );
}

// ─── Shared Styles ───────────────────────────────────────────────────────────
const cardStyle = {
    background: 'var(--card-bg)',
    border: '1px solid var(--card-border)',
    borderRadius: '14px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
};
const thStyle = {
    padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600,
    color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px',
    borderBottom: '1px solid var(--border)',
};
const tdStyle = { padding: '12px', verticalAlign: 'middle' };
